import log from 'loglevel';
import { DefaultResponseBody } from '../../browser-utilities/request-manager';
import { ExitIntentData, ExitIntentEvent } from './exit-intent.model';
import ExitIntentBuilder from './exit-intent-builder';
import { executeHtmlScripts } from '../../browser-utilities/execute-scripts';
import { CommandQueue } from '../../command-queue/command-queue';
import { TriggerManager } from '../../trigger-manager/trigger-manager';
import { SessionStorageManager } from '../../browser-utilities/session-storage-manager';

/**
* Will create a exit intend modal that is displayed when the user moves to mouse outside the window.
* @param responseBody
*/
export function exitIntentListener(responseBody: DefaultResponseBody) {
  if (
    responseBody.exitIntentEnabled &&
    responseBody.exitIntendMessages
  ) {
    ExitIntentManager.triggerNextExitIntent(responseBody.exitIntendMessages);
  };
}

// NOTE: amount of displaying needs not to persist over a session as it will only display again if triggered by BE again which is controlled via freq cappings.

// note:  exit intend needs to be persisted through multiple pages, probably should use session storage for this? (and scramble?)
// note: need to test and check if the excludeDisplayUrls and expire time stuff actually gets passed from BE side...

export class ExitIntentManager {
  private static sessionStorageId = 'ce.exit-intent';
  private static containerElementId = 'ce-exit-intent';
  private static closeButtonSelector = '[data-ce-exit-intent-close]'

  static nextExitIntent: ExitIntentBuilder | null;

  static triggerNextExitIntent(messages: ExitIntentData[]): void {
    if ((Array.isArray(messages) && !messages.length)) {
      log.error('Exit Intent: Format error:', messages);
      return;
    };

    log.debug('Exit Intent triggered: ', messages);

    // note: why does it come in array? Wo only ever display a single exit intent at a time.
    this.storeNextExitIntent(messages[0]);
    this.initNextExitIntent(messages[0]);
  }

  /**
   * Initializes and "arms" the SDK to prepare upcoming exit intent to be executed when the user moves the mouse to the top of the window.
   */
  static initNextExitIntent(message: ExitIntentData): void {
    this.nextExitIntent = new ExitIntentBuilder({
      documentTopOffset: 20,
      delay: message.delayInMs || 200,
      onExitIntent: () => this.showExitIntent(message)
    });
  }

  static checkStoredExitIntent(): void {
    const storedExitIntent = SessionStorageManager.getItem(this.sessionStorageId);
    if (storedExitIntent) {
      this.initNextExitIntent(JSON.parse(storedExitIntent));
    }
  }

  static showExitIntent(message: ExitIntentData): void {
    // CONSIDER: We are now applying a z-index to the template to try to ensure it is on top. z-index will not guarantee it.
    // Ideally we would also offer a target element binding similar to onsite display. Still probably want to keep the z-index for less advanced client solutions.

    let containerEl = document.getElementById(this.containerElementId);

    if(!containerEl) {
      log.debug(`Exit Intent: Creating initial container element with Id '${this.containerElementId}'.`);
      containerEl = document.createElement('div');
      containerEl.id = this.containerElementId;
      containerEl.style.zIndex = '2147483647'; // We need to talk about this. We are not savages here.
      document.body.appendChild(containerEl);
      containerEl.addEventListener('click', () => this.onClickExitIntent(message), false);
    }
    containerEl.innerHTML = message.body;
    executeHtmlScripts(containerEl);
    this.registerAttributeBasedCloseButtons(containerEl);

    CommandQueue.add(() => TriggerManager.eventRequest<ExitIntentEvent>('track', {
      event: 'exit-intent.view',
      campaignId: message.campaignId,
      campaignName: message.campaignName,
      channelConfigId: message.channelConfigId,
      storyId: message.storyId
    }));

    this.removeNextExitIntent();
  }

  private static removeNextExitIntent(): void {
    SessionStorageManager.removeItem(this.sessionStorageId);
    this.nextExitIntent && this.nextExitIntent.cancelListener();
    this.nextExitIntent = null;
  }

  /**
   * pushes the exit intent message to session storage in case it does not get triggered during the
   * current pageview.
   * @param message
   */
  private static storeNextExitIntent(message: ExitIntentData): void {
    SessionStorageManager.setItem(this.sessionStorageId, JSON.stringify(message));
  }

  private static onClickExitIntent(message: ExitIntentData): void {
    CommandQueue.add(() => TriggerManager.eventRequest<ExitIntentEvent>('track', {
      event: 'exit-intent.click',
      campaignId: message.campaignId,
      campaignName: message.campaignName,
      channelConfigId: message.channelConfigId,
      storyId: message.storyId
    }));
  }

  private static registerAttributeBasedCloseButtons(containerEl: HTMLElement | null): void {
    const closeButtons = document.querySelectorAll(this.closeButtonSelector);
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (containerEl && containerEl.innerHTML) {
          containerEl.innerHTML = '';
        }
      }, false);
    });
  }
}
